import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasAcademyAccess } from "@/lib/academyAccess";
import { waLink, btnBrand, money } from "@/lib/academyFormat";
import EnrollmentCard from "@/components/academy/EnrollmentCard";
import EnrollModal from "@/components/academy/EnrollModal";
import StudentModal from "@/components/academy/StudentModal";
import { ACADEMY } from "@/constants/testIds";

// Fix U (2026-10-13): ficha del alumno: contacto, inscripciones y su cuenta.
export default function AcademyStudentPage() {
  const { studentId } = useParams();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [error, setError] = useState("");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const canEnroll = hasAcademyAccess(user, ["executive", "academic", "reception"]);
  const canEdit = hasAcademyAccess(user, ["executive", "academic", "reception", "finance"]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/academy/students/${studentId}`);
      setStudent(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-muted-foreground py-12 text-center">{error}</p>;
  if (!student) return <p className="text-muted-foreground py-12 text-center">Cargando...</p>;
  const wa = waLink(student.phone);
  const formEntries = Object.entries(student.form_data || {}).filter(([, v]) => v !== "" && v != null);

  return (
    <div className="space-y-6" data-testid={ACADEMY.studentPage}>
      <Link to="/academia/alumnos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Alumnos</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground" data-testid={ACADEMY.studentName}>{student.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{student.email} · {student.phone}{student.executive_name ? ` · ejecutivo: ${student.executive_name}` : ""}</p>
          {student.notes && <p className="text-sm text-muted-foreground mt-1">{student.notes}</p>}
          {student.credit_balance > 0 && <p className="text-sm text-[#5B7A54] mt-1" data-testid={ACADEMY.studentCredit}>Saldo a favor para otro curso: {money(student.credit_balance)}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white text-sm text-[#25A244]"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
          {canEdit && <Button variant="outline" onClick={() => setEditOpen(true)} data-testid={ACADEMY.studentEditButton}><Pencil className="w-4 h-4" /> Editar</Button>}
          {canEnroll && <Button className={btnBrand} onClick={() => setEnrollOpen(true)} data-testid={ACADEMY.studentEnrollButton}><Plus className="w-4 h-4" /> Nueva inscripción</Button>}
        </div>
      </div>

      {student.enrollments.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center text-muted-foreground">Todavía no tiene inscripciones.</div>
      ) : (
        <div className="space-y-4">
          {student.enrollments.map((e) => <EnrollmentCard key={e.id} enrollment={e} onChanged={load} />)}
        </div>
      )}

      <div className="bg-white border border-border rounded-xl p-5" data-testid={ACADEMY.studentFormData}>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-3">Datos para la certificación</p>
        {formEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no completó el formulario.</p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {formEntries.map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                {/* Fix W (2026-10-13): los archivos que sube el alumno se abren desde aca */}
                <dd className="text-foreground break-words">{v && typeof v === "object" && v.url ? <a href={getFileUrl(v.url)} target="_blank" rel="noreferrer" className="text-[#8A6A2F] underline">{v.name || "Ver archivo"}</a> : String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <EnrollModal open={enrollOpen} onOpenChange={setEnrollOpen} student={student} onDone={load} />
      <StudentModal open={editOpen} onOpenChange={setEditOpen} student={student} onSaved={load} />
    </div>
  );
}
